"use client";

import { useEffect, useRef, useState } from "react";

// Highlights ticker scroll speed, in px/sec. Used (instead of a fixed
// animation duration) so a short item list and a long one both scroll at the
// same visual pace — a fixed duration made short lists race through and
// leave the track empty for the remainder of the cycle (the "ticker
// disappears for 10s of seconds" / "empty space" reports), since a short
// list's rendered width could be narrower than the visible container.
const TICKER_PX_PER_SEC = 60;
// Breaking-news (/shout, @mention) scroll — slower than the normal ticker so
// a short, important message is actually readable instead of flashing past.
// (Lowered from 40 — still read as "rushing across the screen" at that speed.)
const BREAKING_PX_PER_SEC = 24;
const BREAKING_REPEATS = 3;

export interface TickerItem {
  id: string;
  icon: string;
  text: string;
  highlight?: boolean;
}

export interface BreakingNews {
  id: string;
  icon: string;
  text: string;
}

interface ArenaTickerProps {
  items: TickerItem[];
  matchNumber?: number;
  nameA?: string;
  nameB?: string;
  standings?: { name: string; wins: number; losses: number }[];
  connStatus?: "connected" | "reconnecting" | "disconnected";
  // When set, the normal highlights row is replaced by a BREAKING_REPEATS-
  // times-repeating right-to-left "breaking news" scroll of this one item,
  // then the caller clears it and the normal ticker resumes. See
  // live-arena's shout SSE handler for the timing/sound side of this.
  breakingNews?: BreakingNews | null;
  // Fires once breakingNews's actual scroll duration is known (depends on
  // measured text width), so the caller's auto-clear timer can match it
  // instead of guessing a fixed delay that might cut the scroll off early
  // or leave a long dead gap before the next event.
  onBreakingNewsDuration?: (totalMs: number) => void;
  // Tappable "Chat" entry point — the only way to reach the merged
  // Online/Chat panel now (the cheer bar's count pill collapses/expands the
  // bar instead). Visible on every page, additive to the existing
  // LIVE/reconnecting/disconnected indicator, not a replacement for it.
  onChatClick?: () => void;
  hasUnreadChat?: boolean;
}

export default function ArenaTicker({ items, matchNumber, nameA, nameB, standings = [], connStatus = "connected", breakingNews = null, onBreakingNewsDuration, onChatClick, hasUnreadChat = false }: ArenaTickerProps) {
  // Repeat the item list enough times that the rendered track is always at
  // least 2x the visible container width — short lists (a handful of
  // standings/elimination entries) previously rendered a track narrower than
  // the container, so after the short content scrolled past there was
  // nothing left to show until the fixed-duration animation looped back to
  // 0%, which read as "the ticker disappeared" or "empty space" depending on
  // when you looked. Measuring real widths (instead of a fixed "double it"
  // doubling) makes this correct regardless of item count or text length.
  const trackRef = useRef<HTMLDivElement>(null);
  const oneCopyRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [oneCopyWidth, setOneCopyWidth] = useState(0);

  useEffect(() => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    const observer = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  // trackRef swaps to a different DOM node when breakingNews flips (two
  // separate conditionally-rendered containers share one ref) — re-observe
  // whichever node is actually mounted right now instead of only the one
  // present at first mount.
  }, [breakingNews]);

  useEffect(() => {
    if (!oneCopyRef.current) return;
    setOneCopyWidth(oneCopyRef.current.scrollWidth);
  }, [items]);

  // Must be even — translateX(-50%) needs to land exactly on a repeat
  // boundary for the loop to be seamless.
  const repeatCount = oneCopyWidth > 0
    ? Math.max(2, Math.ceil((containerWidth * 2) / oneCopyWidth / 2) * 2)
    : 2;
  const displayItems = items.length > 0
    ? Array.from({ length: repeatCount }, () => items).flat()
    : items;
  const trackWidthPx = oneCopyWidth * repeatCount;
  // translateX(-50%) of a track built from an even number of repeats lands
  // exactly on a repeat boundary, so the loop point is seamless regardless
  // of how many repeats were needed to fill the container.
  const tickerDurationSec = trackWidthPx > 0 ? (trackWidthPx / 2) / TICKER_PX_PER_SEC : 35;

  // Breaking-news track: a single message repeated BREAKING_REPEATS times.
  // The keyframe moves the track from translateX(100%) to translateX(-100%)
  // — both percentages are relative to the track's OWN width W, so the total
  // pixel distance traveled is 2*W regardless of container size. Measure W
  // so the scroll duration is constant px/sec instead of a fixed 4s that
  // made short shouts flash past unreadably fast and long ones crawl.
  const breakingTrackRef = useRef<HTMLDivElement>(null);
  const [breakingTrackWidth, setBreakingTrackWidth] = useState(0);
  useEffect(() => {
    if (!breakingTrackRef.current || !breakingNews) return;
    const w = breakingTrackRef.current.scrollWidth;
    setBreakingTrackWidth(w);
  }, [breakingNews]);
  const breakingDurationSec = breakingTrackWidth > 0
    ? (breakingTrackWidth * 2) / BREAKING_PX_PER_SEC
    : 6;
  // Tell the caller exactly how long BREAKING_REPEATS full cycles take, so
  // its auto-clear timer matches the real animation instead of a fixed
  // guess that could cut a long message off mid-scroll or leave a long dead
  // gap after a short one finishes.
  useEffect(() => {
    if (!breakingNews || breakingTrackWidth === 0) return;
    onBreakingNewsDuration?.(breakingDurationSec * BREAKING_REPEATS * 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakingNews, breakingTrackWidth]);

  return (
    <div className="w-full" style={{ background: "#0a0318" }}>

      {/* ── Scorebar row ─────────────────────────────────────────────────── */}
      {/* Not a safe-area issue — --safe-area-inset-left/right read 0px on a
          portrait iPhone (no side notch); that env() value is only ever
          nonzero in landscape. Content was getting visually clipped by the
          screen's own rounded corner, which iOS doesn't report via any
          env()/inset value — small fixed margins clear it. Left padding
          matches the BNC badge's yellow (a visual extension of it, since the
          badge sits flush at the very left); right padding stays the row's
          normal dark background since nothing yellow sits at that edge. */}
      <div className="flex items-stretch border-t border-yellow-500/30" style={{ height: "36px", paddingLeft: "10px", paddingRight: "10px", background: "#0a0318" }}>

        {/* BNC badge */}
        <div className="flex items-center shrink-0" style={{ background: "#eab308", marginLeft: "-10px", paddingLeft: "10px", paddingRight: "5px" }}>
          <span className="font-black text-xs tracking-widest uppercase text-black">🏆 BNC</span>
        </div>

        {/* Match info */}
        {nameA && nameB && (
          <div className="flex items-center gap-3 px-4 border-r border-white/10 shrink-0">
            <span className="text-white/50 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
              Match #{matchNumber}
            </span>
            <span className="text-white font-black text-sm">{nameA}</span>
            <span className="text-yellow-400 font-black text-xs">VS</span>
            <span className="text-white font-black text-sm">{nameB}</span>
          </div>
        )}

        {/* Standings */}
        {standings.length > 0 && (
          <div className="hidden sm:flex items-center gap-4 px-4 border-r border-white/10 shrink-0">
            {standings.slice(0, 3).map((s, i) => (
              <div key={s.name} className="flex items-center gap-1">
                <span className="text-white/40 text-xs">{i + 1}.</span>
                <span className={`font-black text-xs ${i === 0 ? "text-yellow-300" : "text-white/80"}`}>{s.name}</span>
                <span className="text-white/30 text-xs">{s.wins}–{s.losses}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {/* Live / status badge */}
        <div className="flex items-center gap-2 px-4 shrink-0 border-l border-white/10">
          {onChatClick && (
            <button
              onClick={onChatClick}
              className={`flex items-center gap-1 mr-1 px-1.5 py-0.5 rounded-md transition-colors ${
                hasUnreadChat ? "text-yellow-300" : "text-purple-300 hover:text-purple-100"
              }`}
              style={hasUnreadChat ? { animation: "chatGlow 1.5s ease-in-out infinite" } : undefined}
              title="Chat / who's online"
            >
              <span className="text-sm leading-none">💬</span>
              <span className="font-black text-xs tracking-widest uppercase">Chat</span>
            </button>
          )}
          {connStatus === "connected" && (
            <>
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-purple-300 font-black text-xs tracking-widest">LIVE</span>
            </>
          )}
          {connStatus === "reconnecting" && (
            <div className="flex items-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-yellow-400"
                  style={{
                    animation: "sequenceDot 1.2s ease-in-out infinite",
                    animationDelay: `${i * 0.3}s`,
                  }}
                />
              ))}
            </div>
          )}
          {connStatus === "disconnected" && (
            <>
              <div className="w-2 h-2 rounded-full bg-red-600" />
              <span className="text-red-400 font-black text-xs tracking-widest">X_X</span>
            </>
          )}
        </div>
      </div>

      {/* Hidden, unrendered measurement copies — same markup/classes as the
          real tracks below so scrollWidth reflects real font metrics, but
          absolutely positioned off-screen so they never affect layout or
          paint. Needed because the real track's width depends on repeatCount,
          which itself depends on this measurement — can't measure the live
          track without creating a circular dependency. */}
      <div style={{ position: "absolute", visibility: "hidden", pointerEvents: "none", whiteSpace: "nowrap" }} aria-hidden>
        {items.length > 0 && (
          <div ref={oneCopyRef} className="flex items-center" style={{ width: "max-content" }}>
            {items.map((item, i) => (
              <span key={`${item.id}-${i}`} className="inline-flex items-center gap-1.5 px-5">
                <span className="text-sm leading-none">{item.icon}</span>
                <span className="text-xs font-semibold">{item.text}</span>
                <span className="ml-3 text-xs">◆</span>
              </span>
            ))}
          </div>
        )}
        {breakingNews && (
          <div ref={breakingTrackRef} className="flex items-center" style={{ width: "max-content" }}>
            <span className="inline-flex items-center gap-1.5 px-8">
              <span className="text-sm leading-none">{breakingNews.icon}</span>
              <span className="text-xs font-black">{breakingNews.text}</span>
            </span>
          </div>
        )}
      </div>

      {/* ── Scrolling ticker row ─────────────────────────────────────────── */}
      {/* Left padding clears the screen's rounded corner (same reasoning as
          the scorebar row above); kept on this row only on the left — the
          right edge is intentionally flush at 0 so the scrolling track can
          run all the way to the screen edge, which looks better for a
          continuously-scrolling ticker than a fixed margin would. The
          BREAKING/HIGHLIGHTS label bleeds its own background color into
          that padding via a matching negative margin, so the inset reads as
          part of the label instead of a visible gap. */}
      {breakingNews ? (
        <div
          key={breakingNews.id}
          className="flex items-stretch border-t border-red-900/60 overflow-hidden"
          style={{ height: "28px", background: "#1a0000", paddingLeft: "32px" }}
        >
          <div className="flex items-center shrink-0 border-r border-red-700/60" style={{ background: "#dc2626", marginLeft: "-32px", paddingLeft: "40px", paddingRight: "5px" }}>
            <span className="text-white font-black text-xs uppercase tracking-widest whitespace-nowrap animate-pulse">
              🚨 BREAKING
            </span>
          </div>
          <div className="flex-1 overflow-hidden flex items-center" ref={trackRef}>
            {/* Gated on a real measured width — rendering the animated track
                immediately (before breakingTrackWidth is known) started the
                CSS animation with the wrong/default 6s duration, then jumped
                to the real duration once measured a beat later. That jump
                read as "text doesn't appear for a while, then suddenly
                shoots across" — the label (BREAKING) appeared instantly
                since it's static, but the scrolling text's first run was
                visibly wrong before self-correcting. A plain static render
                in the gap (rather than nothing) avoids a blank-looking
                pause while the real width is being measured. */}
            {breakingTrackWidth > 0 ? (
              <div
                className="flex items-center whitespace-nowrap"
                style={{
                  width: "max-content",
                  animation: `breakingTicker ${breakingDurationSec}s linear ${BREAKING_REPEATS}`,
                }}
              >
                {Array.from({ length: BREAKING_REPEATS }, (_, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-8">
                    <span className="text-sm leading-none">{breakingNews.icon}</span>
                    <span className="text-xs font-black text-yellow-300">{breakingNews.text}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-4">
                <span className="text-sm leading-none">{breakingNews.icon}</span>
                <span className="text-xs font-black text-yellow-300">{breakingNews.text}</span>
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-stretch border-t border-purple-900/40 overflow-hidden" style={{ height: "28px", paddingLeft: "32px" }}>

          {/* Label */}
          <div className="flex items-center shrink-0 border-r border-purple-700/60" style={{ background: "#6d28d9", marginLeft: "-32px", paddingLeft: "40px", paddingRight: "5px" }}>
            <span className="text-white font-black text-xs uppercase tracking-widest whitespace-nowrap">HIGHLIGHTS</span>
          </div>

          {/* Scroll track */}
          <div className="flex-1 overflow-hidden flex items-center" ref={trackRef}>
            {displayItems.length > 0 ? (
              <div
                className="flex items-center whitespace-nowrap arena-ticker-track"
                style={{ width: "max-content", animation: `ticker ${tickerDurationSec}s linear infinite` }}
              >
                {displayItems.map((item, i) => (
                  <span key={`${item.id}-${i}`} className="inline-flex items-center gap-1.5 px-5">
                    <span className="text-sm leading-none">{item.icon}</span>
                    <span className={`text-xs font-semibold ${item.highlight ? "text-yellow-300" : "text-white/70"}`}>
                      {item.text}
                    </span>
                    <span className="text-purple-600 ml-3 text-xs">◆</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-white/30 text-xs px-4">Season underway — results will appear here</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
